import React from "react";
import { Layout } from "@/components/Layout";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const Methodology = () => {
  usePageSeo({
    title: 'How PropertyTaxHero Works — Our Methodology | PropertyTaxHero',
    description: 'Learn how PropertyTaxHero compiles state and county information, identifies comparable sales, and generates property tax appeal reports. Transparent, free, and fact-based.',
    path: '/methodology',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'PropertyTaxHero Methodology',
      url: 'https://propertytaxhero.com/methodology',
      description: 'How PropertyTaxHero compiles data, finds comparable sales, and generates property tax appeal reports.',
    },
  });

  return (
    <Layout>
      <article className="container py-12 md:py-20 max-w-4xl mx-auto">
        <nav aria-label="Breadcrumb" className="text-sm text-tax-neutralGray mb-6">
          <Link to="/" className="hover:text-tax-brightBlue">Home</Link> &gt; <Link to="/about" className="hover:text-tax-brightBlue">About</Link> &gt; <span>Methodology</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold text-tax-deepBlue mb-6">How PropertyTaxHero Works — Our Methodology</h1>
        <p className="text-lg text-tax-neutralGray mb-10 leading-relaxed">
          PropertyTaxHero helps homeowners evaluate whether their property may be overassessed and prepare a property tax appeal. Here's how our process works, where our data comes from, and how we ensure accuracy.
        </p>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">Where our data comes from</h2>
          <p className="text-tax-neutralGray mb-4">PropertyTaxHero pulls data from publicly available sources, including:</p>
          <ul className="list-disc pl-6 space-y-2 text-tax-neutralGray">
            <li>County assessor websites and public property records</li>
            <li>Public transaction records for recent home sales</li>
            <li>State-level statutes and published appeal procedures</li>
            <li>Third-party data providers that aggregate public assessment data</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">How we identify comparable sales</h2>
          <p className="text-tax-neutralGray mb-4">When you enter your property address, our system identifies recent home sales in your area that are similar to your property in key characteristics:</p>
          <ul className="list-disc pl-6 space-y-2 text-tax-neutralGray">
            <li>Proximity to your property</li>
            <li>Similar square footage, lot size, and bedroom/bathroom count</li>
            <li>Recency of sale (prioritizing the most recent transactions)</li>
            <li>Property type (single-family, condo, townhouse, etc.)</li>
          </ul>
          <p className="text-tax-neutralGray mt-4">You can review, adjust, or remove comps from your report before using it in your appeal.</p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">How state and county guides are compiled</h2>
          <p className="text-tax-neutralGray mb-4">Our state and county property tax appeal guides are compiled from:</p>
          <ul className="list-disc pl-6 space-y-2 text-tax-neutralGray">
            <li>Published state statutes governing property tax appeals</li>
            <li>County assessor office websites</li>
            <li>Official government publications about appeal procedures and deadlines</li>
          </ul>
          <p className="text-tax-neutralGray mt-4">Some content is generated or summarized with the assistance of artificial intelligence and is reviewed for accuracy. However, laws and procedures can change, and we encourage all users to verify deadlines and procedures with their local county assessor's office.</p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">How appeal reports are generated</h2>
          <p className="text-tax-neutralGray mb-4">PropertyTaxHero generates a property tax appeal report that includes:</p>
          <ul className="list-disc pl-6 space-y-2 text-tax-neutralGray">
            <li>Your property's current assessed value</li>
            <li>Comparable sales data supporting a potentially lower value</li>
            <li>A formatted letter you can submit to your county assessor</li>
            <li>Filing instructions specific to your jurisdiction</li>
          </ul>
          <p className="text-tax-neutralGray mt-4">These reports are provided as a starting point. They are not legal documents and do not constitute legal or financial advice.</p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">Important limitations</h2>
          <ul className="list-disc pl-6 space-y-3 text-tax-neutralGray">
            <li>PropertyTaxHero does not guarantee the accuracy of any data, deadline, or procedure. Always verify with your local assessor.</li>
            <li>We do not file appeals on your behalf. You are responsible for submitting your appeal to your county.</li>
            <li>Our comparable sales may not capture every relevant factor about your property (condition, upgrades, view, etc.).</li>
            <li>In <Link to="/non-disclosure-states" className="text-tax-brightBlue hover:underline">non-disclosure states</Link>, sale price data may be limited or unavailable.</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">How content is updated</h2>
          <p className="text-tax-neutralGray">State guides and deadline information are reviewed and updated periodically, typically at the beginning of each tax year. If you notice outdated or inaccurate information, please contact us at <a href="mailto:dev@yourwayhome.com" className="text-tax-brightBlue hover:underline">dev@yourwayhome.com</a>.</p>
        </section>

        <section className="text-center bg-tax-lightGray rounded-lg p-8">
          <h2 className="text-2xl font-semibold text-tax-deepBlue mb-3">Check your assessment for free</h2>
          <p className="text-tax-neutralGray mb-6">Enter your address and PropertyTaxHero will help you evaluate whether you may be overpaying.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-tax-deepBlue hover:bg-tax-brightBlue"><Link to="/#property-form">Get Started Free</Link></Button>
            <Button asChild variant="outline"><Link to="/about">About Us</Link></Button>
          </div>
        </section>
      </article>
    </Layout>
  );
};

export default Methodology;
